import {
  DynamicDocumentItemData,
  InstallationModule,
  InstallationModuleType,
  Inverter
} from "@/types";

export type InverterRecord = {
  module?: InstallationModule;
  answers: DynamicDocumentItemData[];
};

function answer(record: InverterRecord, field: string) {
  return record.answers.find(item => item.field === field)?.value;
}

function amount(record: InverterRecord, minimumPerInverter = 0): number {
  const inverters: Inverter[] = record.module?.data?.inverterConfiguration ?? [];

  return inverters.reduce(
    (total, inverter) => total + Math.max(minimumPerInverter, inverter.amount || 0),
    0
  );
}

/**
 * How many inverters the monteur put on record. Micro inverters come with the panels module and a
 * hybrid string inverter with the meter module; a module's list only counts while its question is
 * answered Ja, because a Nee hides the list and leaves whatever it held behind.
 *
 * The CRM enforces the same rule when a module is finished (MechanicProgress::recordedInverterCount).
 */
export function recordedInverters(panels: InverterRecord, meter: InverterRecord): number {
  let count = 0;

  // "Worden er micro-omvormers geïnstalleerd?" defaults to Ja and is missing from older documents.
  if (answer(panels, "hasMicroInverters") !== false) {
    count += amount(panels);
  }

  if (answer(meter, "hasStringInverter") === true) {
    // The meter module stores at least one of every inverter it lists.
    count += amount(meter, 1);
  }

  return count;
}

/**
 * Whether finishing this module would leave the installation without an inverter on record. Only
 * the module that finishes last can tell: until then the other one may still bring the inverter.
 */
export function invertersMissing(
  finishing: InstallationModuleType,
  panels: InverterRecord,
  meter: InverterRecord
): boolean {
  const others: Partial<Record<InstallationModuleType, InverterRecord>> = {
    "PANELS-REGULAR": meter,
    "METER-REGULAR": panels
  };

  const other = others[finishing]?.module;

  // The installations list knows a module has ended but not what it holds, so wait for the detail.
  if (!other?.ended || !other.data) {
    return false;
  }

  return recordedInverters(panels, meter) === 0;
}
